import { assertNever } from '../../exhaustive';
import { ACK_STATUS, ACK_STATUS_LABEL, COREDUMP_RESET_LABEL, PANEL_STATE, ackStatusLabel, errorLabel } from '../catalog/ack';
import { DALI_MODE_LABEL, daliModeLabel, daliNakLabel } from '../catalog/dali';
import { MAPPING_ACTION_KEYS, MAPPING_ATYPE } from '../catalog/mapping';
import {
  DIN_FIRMWARE_TYPE,
  MODULE_CODE_RADIX,
  DIN_VALUE,
  RCU_CMDTYPE,
  RCU_GROUP,
  RCU_TOP_TYPE,
  dinValueLabel,
  relayValueLabel,
} from '../catalog/protocol';
import { RAIL_TYPE_SPECS } from '../catalog/railType';
import type { RailTypeKey } from '../catalog/railType';
import { formatBytes } from './coredump';
import type { RcuReading } from './readings';
import type {
  RcuCommandDocument,
  RcuDocument,
  RcuMappingDocument,
  RcuSceneDocumentEntry,
  RcuSceneEntry,
} from '../wire';

/**
 * One line describing a document, for the event feed and the console's cards.
 *
 * The technician reads this instead of parsing JSON: what a command does, with
 * the numbers that identify it. Every document the app can build has a line
 * here, and a test asserts that, so a new command cannot arrive without one.
 */

const separator = ' · ';

/** Joins the clauses of a description the same way everywhere. */
const parts = (...values: readonly (string | number)[]): string => values.join(separator);

/** A reply's module, named the same way a request's code is. */
const moduleLabel = (module: number | null): string =>
  module === null ? 'Unknown module' : moduleCodeLabel(moduleCode(module));

/** Two-digit hex, the spelling a module code uses on the wire. */
const moduleCode = (module: number): string =>
  module.toString(MODULE_CODE_RADIX).padStart(2, '0').toUpperCase();

/** The module a request names, by its two-digit code, in the installer's words. */
const moduleCodeLabel = (code: string): string => {
  const match = Object.entries(DIN_FIRMWARE_TYPE).find(([, value]) => value === code);
  if (match === undefined) return `Module ${code}`;
  return RAIL_TYPE_SPECS[match[0] as RailTypeKey].shortLabel;
};

/** How many action fields a scene entry carries. */
const actionCount = (entry: RcuSceneEntry): number =>
  MAPPING_ACTION_KEYS.filter((field) => entry[field] !== undefined).length;

/** What one mapping entry does, in the few words fit for a list row. */
const describeEntry = (entry: RcuSceneDocumentEntry): string => {
  switch (entry.atype) {
    case MAPPING_ATYPE.SCENE:
      return `Scene [${entry.x.join(',')}] with ${actionCount(entry)} actions`;
    case MAPPING_ATYPE.LED_ACK:
      return `LED ack [${entry.x.join(',')}]`;
    case MAPPING_ATYPE.DALI_LED_ACK:
      return `DALI LED ack [${entry.x.join(',')}]`;
    case MAPPING_ATYPE.DELETE:
      return `Delete scene [${entry.x.join(',')}]`;
    case MAPPING_ATYPE.MAP_ERASE:
      return 'Erase mappings and restart';
    case MAPPING_ATYPE.RESTART:
      return 'Restart RCU';
    case MAPPING_ATYPE.WIFI_CONFIG:
      /** The password never appears in a description. */
      return `Legacy Wi-Fi configuration for ${entry.ssid}`;
    default:
      return assertNever(entry);
  }
};

const describeMapping = (document: RcuMappingDocument): string => {
  switch (document.type) {
    case RCU_TOP_TYPE.ADD_MAPPING: {
      const [first] = document.data;
      return first !== undefined && document.data.length === 1
        ? describeEntry(first)
        : `Write ${document.data.length} mappings`;
    }
    case RCU_TOP_TYPE.ADD_LED_MAPPING: {
      const [first] = document.data;
      return first !== undefined && document.data.length === 1
        ? describeEntry(first)
        : `Write ${document.data.length} LED mappings`;
    }
    case RCU_TOP_TYPE.READ_MAPPING:
      return `Read ${document.data.length} scenes`;
    case RCU_TOP_TYPE.ERASE_MAPPING:
      return `Erase ${document.data.length} scenes`;
    case RCU_TOP_TYPE.ERASE_ALL_MAPPINGS:
      return 'Erase all mappings';
    case RCU_TOP_TYPE.READ_LED_MAPPING:
      return `Read ${document.data.length} LED mappings`;
    case RCU_TOP_TYPE.ERASE_LED_MAPPING:
      return `Erase ${document.data.length} LED mappings`;
    default:
      return assertNever(document);
  }
};

const describeCommand = (document: RcuCommandDocument): string => {
  const data = document.data;
  switch (data.cmdtype) {
    case RCU_CMDTYPE.OPERATE_RELAY:
      return parts(`Relay ${data.relayno}`, `group ${data.groupid}`, relayValueLabel(data.value));
    case RCU_CMDTYPE.READ_RELAY:
      return parts(`Read relay ${data.relayno}`, `group ${data.groupid}`);
    case RCU_CMDTYPE.READ_ALL_RELAYS:
      return 'Read all relays';
    case RCU_CMDTYPE.OPERATE_DIN:
      return parts(
        `${moduleCodeLabel(data.dintype)} ${data.addr}/${data.channel}`,
        dinValueLabel(data.value),
      );
    case RCU_CMDTYPE.READ_DIN:
      return `Read ${moduleCodeLabel(data.dintype)} ${data.addr}/${data.channel}`;
    case RCU_CMDTYPE.READ_ALL_DIN:
      return `Read every channel of ${moduleCodeLabel(data.dintype)} ${data.addr}`;
    case RCU_CMDTYPE.OPERATE_DALI:
      return parts(
        `DALI master ${data.master}`,
        `address ${data.daliaddr}`,
        daliModeLabel(data.mode),
        `param ${data.param}`,
      );
    case RCU_CMDTYPE.READ_DALI:
      return parts(`Query DALI master ${data.master}`, `address ${data.daliaddr}`);
    case RCU_CMDTYPE.READ_STATUS:
      return 'Read status';
    case RCU_CMDTYPE.LISTEN_PANEL:
      return 'Listen for panel operations';
    case RCU_CMDTYPE.START_OTA:
      return parts('Start firmware update', data.version);
    case RCU_CMDTYPE.SET_LOGGING:
      return parts(
        data.enable ? 'Console logging on' : 'Console errors only',
        data.mqtt === true ? 'MQTT streaming enabled' : 'MQTT streaming unchanged',
      );
    case RCU_CMDTYPE.READ_LOGGING_STATUS:
      return 'Read logging status';
    case RCU_CMDTYPE.READ_LOGS:
      return `Fetch ${data.lines} log lines`;
    case RCU_CMDTYPE.READ_COREDUMP_INFO:
      return 'Read coredump info';
    case RCU_CMDTYPE.DOWNLOAD_COREDUMP:
      return 'Download coredump';
    case RCU_CMDTYPE.ERASE_COREDUMP:
      return 'Erase coredump';
    case RCU_CMDTYPE.IMPORT_S3_MAPPING:
      return `Import mappings from ${data.url}`;
    default:
      return assertNever(data);
  }
};

/** The command's effect in one line. */
export const describeDocument = (document: RcuDocument): string =>
  document.type === RCU_TOP_TYPE.COMMAND
    ? describeCommand(document)
    : describeMapping(document);

/** One reply, in the same voice: what it says, and which address said it. */
export const describeReading = (reading: RcuReading): string => {
  switch (reading.kind) {
    case 'relayStatus':
      return parts(
        `Relay ${reading.relay}`,
        `group ${reportGroupLabel(reading.group)}`,
        relayValueLabel(reading.value),
      );
    case 'allRelays':
      return parts(
        'All relays',
        `${reading.values.filter((value) => value !== DIN_VALUE.MIN).length} on`,
      );
    case 'relayAck':
      return parts(`Relay ${reading.relay}`, ackStatusLabel(reading.status));
    case 'dinAck':
      return parts(
        `${moduleLabel(reading.module)} ${reading.addr}/${reading.channel}`,
        ackStatusLabel(reading.status),
      );
    case 'dinStatus':
      return parts(
        `${moduleLabel(reading.module)} ${reading.addr}/${reading.channel}`,
        dinValueLabel(reading.value),
      );
    case 'dinStatusAll':
      return parts(
        `${moduleLabel(reading.module)} ${reading.addr}`,
        `${reading.values.length} channels`,
      );
    case 'daliOperateAck':
      return parts(
        `DALI master ${reading.master}`,
        `address ${reading.address}`,
        daliModeLabel(reading.mode),
        ackStatusLabel(reading.status),
      );
    case 'daliAck': {
      const outcome = reading.success
        ? DALI_MODE_LABEL[reading.mode]
        : `${DALI_MODE_LABEL[reading.mode]} ${ACK_STATUS_LABEL[ACK_STATUS.FAIL]}`;
      const detail = reading.success ? `${reading.value}` : daliNakLabel(reading.value);
      return parts(`DALI master ${reading.master}`, `address ${reading.address}`, outcome, detail);
    }
    case 'panel':
      switch (reading.event.state) {
        case PANEL_STATE.STARTED:
          return 'Panel watch started';
        case PANEL_STATE.TIMEOUT:
          return 'Panel watch timed out';
        case PANEL_STATE.OPERATION:
          return parts(
            'Panel operation',
            `${reading.event.group}/${reading.event.addr}/${reading.event.channel}`,
            relayValueLabel(reading.event.value),
          );
        default:
          return assertNever(reading.event);
      }
    case 'notify':
      return parts(
        'Service panel',
        `${reading.group}/${reading.addr}/${reading.channel}`,
        `${reading.value}`,
      );
    case 'status':
      return parts(
        'Status',
        reading.status,
        reading.serial === '' ? 'no serial' : reading.serial,
        reading.ip === '' ? 'no address' : reading.ip,
      );
    case 'commandResult':
      return parts(reading.statusType, ackStatusLabel(reading.status));
    case 'mappingRead':
      return parts(`${reading.entries.length} scenes read`);
    case 'ledMappingRead':
      return parts(`${reading.entries.length} LED mappings read`);
    case 'coredumpChunk':
      return parts(
        `Coredump ${reading.dumpId}`,
        `chunk ${reading.seq + 1}/${reading.total}`,
        formatBytes(reading.size),
      );
    case 'coredumpInfo':
      return reading.present
        ? parts(
            `Coredump ${reading.dumpId}`,
            formatBytes(reading.size),
            COREDUMP_RESET_LABEL[reading.reset],
          )
        : parts('No coredump stored', COREDUMP_RESET_LABEL[reading.reset]);
    case 'coredumpAck': {
      if (reading.error !== '') return parts('Coredump refused', errorLabel(reading.error));
      if (reading.erased) return 'Coredump erased';
      return parts('Coredump upload', ackStatusLabel(reading.status), `${reading.chunks ?? 0} chunks`);
    }
    case 'otaProgress':
      return parts('Firmware update', `${reading.progress}%`);
    case 'otaAck':
      return parts('Firmware update', ackStatusLabel(reading.status));
    case 'loggingAck':
      return parts('Logging', ackStatusLabel(reading.status), `MQTT streaming ${reading.mqtt}`);
    case 'loggingStatus':
      return parts('Logging', `console ${reading.enabled}`, `MQTT streaming ${reading.mqtt}`);
    case 'logsAck':
      return parts('Log fetch', ackStatusLabel(reading.status), `${reading.pending ?? 0} pending`);
    case 's3Progress':
      return parts('Mapping import', `${reading.progress}%`);
    case 's3Ack':
      return reading.error !== ''
        ? parts('Mapping import failed', errorLabel(reading.error))
        : parts(
            'Mapping import',
            ackStatusLabel(reading.status),
            `${reading.scenes ?? 0} scenes`,
            `${reading.led ?? 0} LED`,
            `${reading.dali ?? 0} DALI`,
          );
    case 'rxFrame':
      return parts('Keypad frame', `${reading.bytes.length} bytes`, `status ${reading.statusByte}`);
    case 'unknown':
      return parts('Unreadable', reading.reason);
    default:
      return assertNever(reading);
  }
};

/** Group a report names, or the group ordinary RCU use means when it omits one. */
const reportGroupLabel = (group: number | null): number | string =>
  group ?? `default ${RCU_GROUP.DEFAULT}`;
